import {useMemo} from "react";
import cn from "classnames";

import {useSetting} from "contexts/setting";
import {ALL_PRODUCT, RELEASES_SETS} from "data/constants";
import {useCurrentSet} from "hooks/useCurrentSet";
import {type BannerListScope} from "libs/bannerComposition";
import {withSlotTemplateId, withSlotTemplateIds} from "data/templateSlots";
import {useTemplateSelection} from "hooks/useTemplateSelection";
import {
  getOtherSetTemplates,
  getReleaseProductCodesForType,
  getSetTemplatesForProducts,
} from "utils/releases";
import {getRouteTemplates} from "utils/routeTemplates";
import CheckboxGroup from "./CheckboxGroup";

/**
 * The banners of this page for a period, as an admin chooses them
 * (libs/bannerComposition.ts). A cleared banner goes off the page: users do
 * not see it at all and cannot turn it back on. On releases an admin also
 * adds banners of other sets; on the ALL-products view an added banner shows
 * for every product.
 *
 * Each change shows at once and saves at once, as the selector options above
 * do: `onSave` writes both lists to the default of the Scope, Level and
 * Stages the panel names (`saveTargetLabel`). So a stage, a product and a set
 * each can have their own banners. A saved scene keeps the banners it was
 * saved with.
 */

interface Banner {
  id: string;
  label: string;
}

interface Option {
  label: string;
  value: string;
}

/** The two banner lists, as a change leaves them. */
export interface BannerLists {
  hiddenTemplates: string[];
  extraTemplates: string[];
}

interface Props {
  onSave: (lists: BannerLists) => void;
  status: "saved" | "failed" | null;
  /** Where a change saves: "IntelliJ IDEA · Beta · set social-media". */
  saveTargetLabel: string;
  /** No change can save yet: the defaults have not loaded. */
  disabled: boolean;
}

export default function BannerCompositionSection({
  onSave,
  status,
  saveTargetLabel,
  disabled,
}: Props) {
  const {
    dataChannel,
    dataType,
    product,
    layoutOption,
    hiddenTemplates,
    extraTemplates,
    editHiddenTemplates,
    editExtraTemplates,
  } = useSetting() as unknown as {
    dataChannel: string;
    dataType: string;
    product: Option;
    layoutOption: number | string;
    hiddenTemplates: string[];
    extraTemplates: string[];
    editHiddenTemplates: (ids: string[]) => void;
    editExtraTemplates: (ids: string[], scope: BannerListScope) => void;
  };
  const currentSet = useCurrentSet() as string | undefined;
  const isReleases = dataChannel === "releases";
  const isAllProducts = product.value === ALL_PRODUCT.value;

  // The ALL-products view draws each banner for every product of the stage.
  const scope: BannerListScope = useMemo(
    () => ({
      isAllProducts,
      productCodes: isAllProducts ? getReleaseProductCodesForType(dataType) : [],
    }),
    [isAllProducts, dataType]
  );
  const productValues = useMemo(
    () => (isAllProducts ? [...scope.productCodes] : [product.value]),
    [isAllProducts, scope, product.value]
  );

  const templateSelection = useTemplateSelection();

  // The page's own banners, before an admin removed any.
  const ownBanners: Banner[] = useMemo(
    () =>
      isReleases
        ? getSetTemplatesForProducts({
            selectedSet: currentSet,
            layoutOption,
            productValues,
            selection: templateSelection,
          })
        : getRouteTemplates(dataChannel, {
            layoutOption,
            set: currentSet,
            product: product.value,
            type: dataType,
            selection: templateSelection,
          }),
    [
      isReleases,
      currentSet,
      layoutOption,
      productValues,
      dataChannel,
      product.value,
      dataType,
      templateSelection,
    ]
  );

  const otherSets = useMemo(
    () =>
      isReleases
        ? getOtherSetTemplates({
            selectedSet: currentSet,
            layoutOption,
            productValues,
            sets: RELEASES_SETS,
            selection: templateSelection,
          })
        : [],
    [isReleases, currentSet, layoutOption, productValues, templateSelection]
  );

  if (ownBanners.length === 0 && otherSets.length === 0) return null;

  // An id names its place on the page: a removed or added "Splash" is the
  // design on air there too (data/templateSlots.ts).
  const removed = new Set(withSlotTemplateIds(dataChannel, hiddenTemplates));
  const added = new Set(withSlotTemplateIds("releases", extraTemplates));
  const removedCount = ownBanners.filter((banner) => removed.has(banner.id)).length;
  const addedCount = otherSets
    .flatMap((group) => group.templates)
    .filter((banner) => added.has(banner.id)).length;

  return (
    <div className="default-scene-control selector-visibility-section banner-composition-section">
      <span
        className="default-scene-caption"
        title="The banners users get on this page. Each change saves at once for the Scope, Level and Stage above."
      >
        Banners on this page
      </span>

      {ownBanners.length > 0 && (
        <CheckboxGroup
          title="Banners of this page"
          summary={
            removedCount > 0
              ? `${removedCount} of ${ownBanners.length} removed`
              : `all ${ownBanners.length} on`
          }
          isChanged={removedCount > 0}
        >
          {ownBanners.map((banner) => (
            <label
              key={banner.id}
              className={cn("selector-visibility-item", {"is-hidden": removed.has(banner.id)})}
              title={banner.label}
            >
              <input
                type="checkbox"
                checked={!removed.has(banner.id)}
                disabled={disabled}
                onChange={(e) => {
                  const next = withSlotTemplateId(
                    dataChannel,
                    hiddenTemplates,
                    banner.id,
                    !e.target.checked
                  );
                  editHiddenTemplates(next);
                  onSave({hiddenTemplates: next, extraTemplates});
                }}
              />
              <span className="selector-visibility-label">{banner.label}</span>
            </label>
          ))}
        </CheckboxGroup>
      )}

      {otherSets.length > 0 && (
        <CheckboxGroup
          title="Add from other sets"
          summary={addedCount > 0 ? `${addedCount} added` : "none added"}
          isChanged={addedCount > 0}
        >
          {otherSets.map(({set, templates}) => (
            <div key={set.value} className="banner-composition-set">
              <span className="banner-composition-set-label">{set.label}</span>
              {templates.map((banner: Banner) => (
                <label
                  key={banner.id}
                  className={cn("selector-visibility-item", {"is-added": added.has(banner.id)})}
                  title={`${banner.label} (${set.label})`}
                >
                  <input
                    type="checkbox"
                    checked={added.has(banner.id)}
                    disabled={disabled}
                    onChange={(e) => {
                      const next = withSlotTemplateId(
                        "releases",
                        extraTemplates,
                        banner.id,
                        e.target.checked
                      );
                      editExtraTemplates(next, scope);
                      onSave({hiddenTemplates, extraTemplates: next});
                    }}
                  />
                  <span className="selector-visibility-label">{banner.label}</span>
                </label>
              ))}
            </div>
          ))}
        </CheckboxGroup>
      )}

      {status === "saved" && (
        <span className="default-scene-status success banner-composition-status">
          Saved for {saveTargetLabel}
        </span>
      )}
      {status === "failed" && (
        <span className="default-scene-status warning banner-composition-status">
          Save failed: the banners changed on this page only
        </span>
      )}
      {status === null && (
        <span className="banner-composition-note">
          Each change saves at once for {saveTargetLabel}.
        </span>
      )}
    </div>
  );
}
